import type * as THREE from 'three';
import type { BeatPartLoop, BeatPartWorld } from '@/game/beatPartModule';
import { bellEmissionRadius, HOLLY_BELL_STRIDE_METRES } from '@/audio/sounds/bell';
import { applyDogPose } from '@/anim/dogPose';
import { HollyRig } from '@/anim/hollyRig';
import { dogAsset, findDogParts, type DogParts } from '@/models/dog';
import { hollyAsset, HOLLY_BELL_HEIGHT, HOLLY_BODY_RADIUS } from '@/models/holly';
import { HollyPresenter, drainHollyBellQueue } from '@/game/hollyPresenter';
import { TwelveOutVoice } from '@/game/beats/twelveOutVoice';
import {
  BOARDS_AT, CULVERT_ROUTE_LENGTH, DITCH_AT, DOG_POST_GUARD_RADIUS, HEDGEROW_BOUNDS, HERD_BALK_DISTANCE, HOLLY_LIE_UP, NIGHT_DOG_AT,
  culvertRoutePlace, hedgerowCompanionObstacles, hedgerowGroundY,
} from '@/world/hedgerow';
import { bakeHedgerowNightLightGrid, hedgerowCover, hedgerowNightWatcher } from '@/world/hedgerowStealth';
import { NightHuddle } from '@/world/nightHuddle';
import { FadeOverlay } from '@/world/fadeOverlay';
import { ARRIVAL_RADIUS, TWELVE_OUT_CUES, TWELVE_OUT_FLAGS, TwelveOut } from '@/story/act2/twelveOut';
import { CulvertQueue, DRAW_RANGE, SCHEDULE as CULVERT_SCHEDULE } from '@/story/act2/culvertQueue';
import { NightDog, type NightDogEvent } from '@/story/act2/nightDog';
import type { CompanionBellEvent } from '@/systems/companionBell';
import { StealthSystem, exposureAt, type CoverVolume, type LightGrid, type Position } from '@/systems/stealth';

/**
 * Beat 10's clock, as something the player can hear coming.
 *
 * `docs/beat-10-plan.md` stage D: *"the dogs and the floodlights arrive on an
 * authored schedule the player can hear coming."* The floodlights are first on
 * that plan's own cut line and are cut; what is left is **a handler whistling
 * his dogs in, four times, closer each time**, and one existing sound carries
 * all four.
 *
 * ── Why a whistle and not a bark, which is the obvious choice ───────────────
 *
 * There is no bark in this game and `src/audio/sounds/dog.ts` says so in its
 * header — *"there is deliberately no bark"* — because beat 4's dog is
 * frightening for being ordinary and competent rather than for performing. Beat
 * 10 does not get to overturn that in a stage that is about a queue, and adding
 * one is an audio job with its own direction document and its own gate.
 *
 * The whistle is also the better sentence. `handler-whistle` is a **person**
 * calling animals to a sound, four times, walking down the field — which is the
 * exact mirror of what the player has been doing with `F` since stage 2, and
 * S-1 is the reason that matters. The thing coming for her is doing her own
 * trick back at her.
 *
 * The positions walk from the yard end (z 12, behind her at the spawn) down to
 * the top of the bank (z −14), so the approach is legible by direction as well
 * as by level; y is 1.6 m because it is a standing man's mouth. The gains rise
 * *on top of* the engine's distance attenuation rather than instead of it — the
 * last one is not merely nearer, it is louder, because he is no longer calling
 * across a field to dogs he expects to come.
 *
 * **Every id here must exist in `CULVERT_SCHEDULE`**, and the assertion below is
 * the gate that says so: a cue authored in one place and not the other is a
 * silent nothing at the moment the beat most needs a sound.
 */
const CULVERT_CUE_AT: Readonly<Record<string, { position: Position; gain: number }>> = {
  far: { position: { x: 4, y: 1.6, z: 12 }, gain: 0.30 },
  near: { position: { x: 0, y: 1.6, z: 2 }, gain: 0.42 },
  closing: { position: { x: -4, y: 1.6, z: -8 }, gain: 0.55 },
  arrive: { position: { x: -8, y: 1.6, z: -14 }, gain: 0.70 },
};
for (const entry of CULVERT_SCHEDULE) {
  if (CULVERT_CUE_AT[entry.id] === undefined) {
    throw new Error(`beat 10: schedule cue "${entry.id}" has no authored position`);
  }
}
// This assertion is the gate: 16.978 m - 7.835 m = 9.14 m to the pipe, inside
// DRAW_RANGE 10. Raising the standoff past about 3 m creates a false `range`
// refusal instead of the `blocked` refusal the dog is meant to author.
if (CULVERT_ROUTE_LENGTH - HERD_BALK_DISTANCE > DRAW_RANGE) {
  throw new Error('beat 10: herd balk leaves the pipe beyond its draw range');
}

/** Where she is, as the host hands it in. Read, never kept. */
interface NightPlayer { readonly x: number; readonly y: number; readonly z: number }

/** `GameHandle.twelveOutDebug`'s shape. */
export type TwelveOutDebug = ReturnType<HedgerowNight['debug']>;

/**
 * Beat 10, the night field: what it builds, how it ticks, and how it is shown.
 *
 * **The host still decides WHEN each step runs, and the order is load-bearing.**
 * `TWELVE_OUT_PART_MODULE` maps each method to the part hook the host calls at
 * that position, inside one fixed tick: `addStoryFlags` in `fixedStep`, before
 * the story; `stepCompanion` in `beforePhysics`; `stepWatch` and `stepQueue`
 * in `afterPhysics` — with her placement between those two when the dog catches
 * her. On the render step `renderWorld` runs in `renderStep` and `renderLate`
 * in `renderLate`, after the listener has moved. Those are the positions the
 * inline blocks held in `main.ts` before session 77; moving any of them across
 * `physics.step` is a gameplay change, not a refactor (the engine audit's
 * migration rule).
 *
 * The story, the huddle interactable, the planes and the beat fade are the
 * module's own. What neither knows is anything inside those steps: the bell's
 * stealth emission, the dog's barrier, the queue's cues, the loops and their
 * teardown latches all live here.
 */
export class HedgerowNight {
  readonly hollyRoot: THREE.Group;
  readonly hollyRig: HollyRig;
  readonly hollyPresenter: HollyPresenter;
  readonly dogRoot: THREE.Group;
  readonly dogParts: DogParts;
  readonly dog: NightDog;
  /** The turn-back's black: only the dog's `out` phase shows it. */
  readonly dogFade = new FadeOverlay(0);
  readonly twelveOut: TwelveOut;
  readonly stealth: StealthSystem;
  /** The exact objects the live watcher samples; debug must never re-bake them. */
  readonly grid: LightGrid;
  readonly cover: readonly CoverVolume[];
  readonly queue = new CulvertQueue({ routeLength: CULVERT_ROUTE_LENGTH });
  readonly huddle = new NightHuddle();
  readonly #audio: Pick<BeatPartWorld, 'playOneShot' | 'playLoop'>;
  readonly #voice: TwelveOutVoice;
  /** Held once: the bell drain runs on frames that ring, and a closure there is per-frame garbage. */
  readonly #playOneShot: BeatPartWorld['playOneShot'];

  #captures = 0;
  #bellEvents = 0;
  #lastBell: { thresholdDistance: number; x: number; z: number; radius: number } | null = null;
  /** Simulation decides the strokes on the fixed step; `renderLate` voices them (D-045). */
  readonly #bellQueue: CompanionBellEvent[] = [];
  readonly #dogEvents: NightDogEvent[] = [];
  // The exact fixed-step input handed to the queue; debug reads this rather
  // than recomputing a dog snapshot after it may already have moved again.
  #barrier: number | null = null;
  /**
   * Schedule cues waiting to be voiced, drained on the RENDER step.
   *
   * The same split D-161 paid for and the same one `#bellQueue` above
   * uses: the queue is simulation and runs at a fixed 60 Hz whatever the display
   * is doing, while `playOneShot` is presentation and belongs on the frame.
   *
   * **The first version of this note claimed it prevented four handlers
   * whistling at once during a catch-up**, and that is not a failure this module
   * can produce: `#fireSchedule` walks a cursor, so each entry emits exactly one
   * event ever, however many ticks run. An independent review caught it. The
   * split is still right and the honest reason is the smaller one — the fixed
   * step must not touch the audio engine, because that is the seam.
   */
  readonly #cueQueue: string[] = [];
  /**
   * True once beat 10 has said `end`, and it exists because stopping is not enough.
   *
   * `TWELVE_OUT_CUES.end` stops the herd bed and the dog's pant, and **the very
   * next render frame started both again**: `TwelveOutVoice.update` creates the bed
   * whenever the clock is running and the herd has not ended, and she may go
   * through the pipe long before the schedule's terminal — so with `?chain=0`
   * the field went on shuffling behind the black screen until 52 s had passed.
   * Found on independent review, and it is D-087/D-090 in its fifth costume: a
   * teardown that a per-frame updater can undo is not a teardown.
   *
   * It is a latch rather than a check on `story.finished` because the two are
   * not the same moment — `end` is emitted on the stage's `exit`, and the beat
   * is finished a tick later. The sound has to stop with the cue.
   *
   * `beat-10-close.spec.ts`'s quiet arm is what holds it: the bed must be
   * silent after the close **while `herd.ended` is still false**, which is the
   * only window in which the restart could happen at all.
   */
  #closed = false;
  /**
   * True from `fadeOut`, which is four seconds earlier than `#closed`.
   *
   * It exists for one thing: **`CALL  F` was still on screen on the frame she
   * went into the pipe.** The verb-prompt suppressor takes each beat's fade at
   * `> 0.5`, which is the right threshold for a beat that ends in the open —
   * half a second of prompt under a fade nobody is reading — and the wrong one
   * here, because the frame it survives onto is the last frame of the beat and
   * the verb it offers has no meaning left. Found by generating the close's
   * evidence frames and looking at them, which is the only thing that could
   * have.
   *
   * Separate from `#closed` rather than folded into it because the two
   * are different moments and each is used at exactly one: the sound stops with
   * `end`, the HUD stops with the fade starting.
   */
  #closing = false;
  #pant: BeatPartLoop | null = null;
  #dogRenderTime = 0;

  constructor(options: Pick<BeatPartWorld, 'scene' | 'lineOfSight' | 'playOneShot' | 'playLoop'>) {
    this.#audio = options;
    this.#voice = new TwelveOutVoice(options);
    this.#playOneShot = options.playOneShot;
    this.hollyRoot = hollyAsset.create();
    this.hollyRoot.position.set(HOLLY_LIE_UP.x, hedgerowGroundY(HOLLY_LIE_UP.x, HOLLY_LIE_UP.z), HOLLY_LIE_UP.z);
    this.hollyRoot.rotation.y = HOLLY_LIE_UP.yaw;
    options.scene.add(this.hollyRoot);
    this.hollyRig = new HollyRig(this.hollyRoot);
    this.hollyPresenter = new HollyPresenter(this.hollyRoot, this.hollyRig);
    this.dogRoot = dogAsset.create();
    this.dogParts = findDogParts(this.dogRoot);
    this.dogRoot.position.set(NIGHT_DOG_AT.x, hedgerowGroundY(NIGHT_DOG_AT.x, NIGHT_DOG_AT.z), NIGHT_DOG_AT.z);
    this.dogRoot.rotation.y = NIGHT_DOG_AT.facing;
    options.scene.add(this.dogRoot);
    this.dog = new NightDog({ post: NIGHT_DOG_AT, bankZ: DITCH_AT.nearBankZ, fenceZ: BOARDS_AT.z, bounds: HEDGEROW_BOUNDS });
    const obstacles = hedgerowCompanionObstacles();
    const west = obstacles.find((obstacle) => obstacle.id === 'fence-west-run');
    const east = obstacles.find((obstacle) => obstacle.id === 'fence-east-run');
    if (west === undefined || east === undefined) throw new Error('beat 10 requires both field-fence runs for Holly routing');
    if (BOARDS_AT.x <= west.max.x || BOARDS_AT.x >= east.min.x) throw new Error('beat 10 broken boards must remain inside the field-fence gap');
    this.twelveOut = new TwelveOut({
      lieUp: HOLLY_LIE_UP, obstacles,
      gap: {
        minX: west.max.x, maxX: east.min.x, fenceZ: BOARDS_AT.z,
        north: { x: BOARDS_AT.x, z: Math.max(west.max.z, east.max.z) + HOLLY_BODY_RADIUS + ARRIVAL_RADIUS },
        south: { x: BOARDS_AT.x, z: Math.min(west.min.z, east.min.z) - HOLLY_BODY_RADIUS - ARRIVAL_RADIUS },
      },
      bellStrideDistance: HOLLY_BELL_STRIDE_METRES, bellSoundRadius: bellEmissionRadius(),
    });
    this.grid = bakeHedgerowNightLightGrid();
    this.cover = hedgerowCover();
    this.stealth = new StealthSystem(this.grid, this.cover, [hedgerowNightWatcher()], options.lineOfSight);
    options.scene.add(this.huddle.group);
  }

  // ── Fixed step ───────────────────────────────────────────────────────────

  /**
   * The story's one room fact that is not a plane: the herd is moving.
   *
   * **One tick stale, and that is fine.** The queue is stepped further
   * down this same fixed update, so this reads the clock as of the end of
   * the previous tick. Against a `MUSTER_DISTANCE` of half a metre at
   * `HERD_SPEED` 1.55 that is 26 mm of herd, and the flag it feeds is a
   * stage transition rather than a measurement. Moving the read below the
   * step would put a story flag downstream of the dog's turn-back branch,
   * which is where a teleport happens.
   */
  addStoryFlags(raiseFlag: (flag: string) => void): void {
    if (this.queue.snapshot().clock !== null) raiseFlag(TWELVE_OUT_FLAGS.theyAreMoving);
  }

  /**
   * Beat 10's one verb. A toggle, and the module owns which way it currently
   * points; the host only says that `F` was pressed.
   */
  toggleCall(): void { this.twelveOut.toggleCall(); }

  /**
   * Holly and her bell, BEFORE `physics.step`.
   *
   * **Stealth on the fixed step, audio on the render step**, and the split
   * is D-045: a watcher's suspicion is game state and has to be identical
   * with the audio muted. `TwelveOut.step` feeds the bell every tick it
   * steps the companion, including the still ones, because `advance()`
   * rejects a pair that does not continue the trace it last accepted.
   */
  stepCompanion(dt: number, self: NightPlayer): void {
    for (const event of this.twelveOut.step(dt, { x: self.x, z: self.z })) {
      const position = { x: event.position.x, y: HOLLY_BELL_HEIGHT, z: event.position.z };
      this.#bellEvents += 1;
      this.#lastBell = { thresholdDistance: event.thresholdDistance, x: event.position.x, z: event.position.z, radius: event.radius };
      // **This is the line beat 9 could not gate.** The shed constructs no
      // `StealthSystem` (D-153), so its own copy of this call is inert and
      // `companion-bell.spec.ts` proves the consequence with a system it
      // supplies itself — deleting the call there leaves the suite green.
      // Here it is a live watcher in a live room, and
      // `beat-10-call.spec.ts` asserts the delta across it. Deleting this
      // line makes that spec fail — by its `fastForwardUntil` running out,
      // *"condition still false after 40 s of SIMULATION time"*, since the
      // suspicion it waits on never rises. Watched rather than assumed
      // (D-100). **This comment named the wrong failure for one commit**:
      // it still quoted `Expected: > 0 / Received: 0` from an earlier
      // version of the test, which an independent review caught. A comment
      // that quotes a failure has to be re-read when the test changes.
      this.stealth.emitSound({ position, radius: event.radius });
      this.#bellQueue.push(event);
    }
  }

  /**
   * The dog's watch, AFTER `physics.step`. Returns true when the dog has
   * caught her; the host then puts her body back at `HEDGEROW_NIGHT_SPAWN`
   * before it calls `stepQueue`, because the controller is the host's.
   *
   * After `physics.step`, with beat 4's and beat 6's ticks and for the
   * same reason: `hasLineOfSight` is a Rapier `castRay` and Rapier
   * rebuilds its spatial acceleration structure inside `world.step()`.
   *
   * Suspicion is therefore evaluated against the dog's position at the end
   * of the previous tick: at 1.9 m/s that is 32 mm, while moving first
   * would make the decision read a snapshot another tick older still.
   */
  stepWatch(dt: number, self: NightPlayer): boolean {
    const snapshot = this.stealth.step(dt, { x: self.x, y: self.y, z: self.z });
    this.#dogEvents.push(...this.dog.step(dt, self, snapshot.watchers.find((watcher) => watcher.watcherId === 'night-dog')));
    const dog = this.dog.snapshot();
    this.stealth.moveWatcher('night-dog', { x: dog.x, y: hedgerowGroundY(dog.x, dog.z), z: dog.z }, dog.facing);
    if (!dog.placeRosalie) return false;
    this.#recoverFromCapture();
    return true;
  }

  /**
   * Restore the field after the dog catches Rosalie — everything except her
   * body, which the host moves (see `stepWatch`).
   *
   * It resets Holly together with her bell cadence and call direction, returns
   * the dog to its post (while preserving an active turn-back timeline), and
   * clears the dog's suspicion. `capture-recovery.spec.ts` holds each of those
   * facts. None of the three reads her position, so running them before the
   * host places her is the same as after.
   *
   * It deliberately preserves the culvert queue and its clock: that is what
   * being caught costs in this beat (seconds off a clock that keeps running, not
   * a rewind), and `capture-recovery.spec.ts` proves the queue's present and
   * future progress. Everything else it simply does not write — the huddle, the
   * cue and voice queues, the camera, the story sequencer — which is a property
   * of this body, not of any gate. Unlike `resetForRespawn`, this is a capture
   * consequence rather than a part reset.
   */
  #recoverFromCapture(): void {
    this.twelveOut.reset();
    this.dog.reset();
    this.stealth.resetSuspicion('night-dog');
    this.#captures += 1;
  }

  /**
   * The queue, and the ONE thing it knows about the room.
   *
   * `culvertRoutePlace` is the whole seam: it turns her position into a
   * place on the polyline — how far along, and how far off — and the module
   * decides everything else from that. Which is why the count is a function
   * of the input trace and not of anything authored (S-7): the trace IS her
   * path down the field, sampled at 60 Hz.
   *
   * Stepped AFTER the dog's turn-back branch on purpose. `placeRosalie`
   * teleports her back up the field, and a queue stepped before it would
   * read the position she was caught at and draw the herd one tick toward a
   * leader who is no longer there.
   */
  stepQueue(dt: number, self: NightPlayer): void {
    // ── The dog is the herd's barrier, and this is where it becomes one ──
    //
    // `distanceFromPost()` rather than `snapshot()`: this runs every fixed
    // tick and `snapshot()` allocates, which the beat's plan forbids here.
    // It is read AFTER `stepWatch` on purpose — a capture there calls
    // `dog.reset()`, which puts the animal back on its post, and a barrier
    // taken before it would report a way down that had just closed.
    this.#barrier = this.dog.distanceFromPost() <= DOG_POST_GUARD_RADIUS ? HERD_BALK_DISTANCE : null;
    for (const event of this.queue.step(dt, culvertRoutePlace(self.x, self.z), this.#barrier)) {
      if (event.kind === 'cue') this.#cueQueue.push(event.id);
      else if (event.kind === 'stall' || event.kind === 'regain') this.#voice.enqueue(event);
    }
  }

  /**
   * The cues the room acts on. The story and the beat's fade belong to
   * `TWELVE_OUT_PART_MODULE` (`src/game/beats/twelveOut.ts`); the host forwards
   * every cue to both.
   */
  handleCue(cue: { id: string }): void {
    if (cue.id === TWELVE_OUT_CUES.fadeOut) this.#closing = true;
    if (cue.id === TWELVE_OUT_CUES.end) {
      // ── D-087/D-090 for the fifth time, and this beat has two loops ────────
      //
      // An `AudioHandle` left running keeps running. Beat 5 shipped a plant that
      // went on thumping behind the black screen at the end of Act 1, and beat
      // 10 hands over to the credits, so a herd still shuffling under them is
      // the same defect with a worse audience.
      //
      // **The herd bed can outlive `herd.ended`**: she may go through the pipe
      // long before the schedule's terminal, in which case nothing has ended
      // anything and the bed is at full gain on the frame the screen goes black.
      //
      // **The latch is set FIRST and the first version of this did not have
      // one.** Both loops have a per-frame updater that recreates them —
      // `TwelveOutVoice.update` while the clock runs and the herd has not ended,
      // `#updatePant` while the animal is still walking — so stopping
      // them here bought exactly one frame of silence. Found on independent
      // review; see `#closed`.
      //
      // 1.2 s is the release `TwelveOutVoice` uses for the same handle,
      // so the herd fades the way it does everywhere else; the dog gets beat 6's
      // and beat 7's 2.5.
      this.#closed = true;
      this.#voice.close();
      this.#pant?.stop(2.5);
      this.#pant = null;
    }
  }

  /**
   * `respawn()`: put the beat back where it started — the harness, the console,
   * a restart. Her body is the host's; everything here but the dog goes back.
   *
   * ── AND NOT THE DOG, WHICH IS DELIBERATE AND WAS NEARLY "FIXED" ───────
   *
   * Everything goes back to the start and the one thing in the room
   * with a position of its own does not, which reads exactly like an
   * omission. It was written as one; it is load-bearing now. **Three gates
   * in `beat-10-dog.spec.ts` use `respawn` as "teleport her and keep the
   * world"** — they put her on the open fall until the dog latches a drive,
   * move her out of its range, and assert the drive still steers at the
   * frozen last-seen point; they move her between the channel cover and the
   * open bank to compare the suspicion each gains from a dog that must not
   * have gone home in between. Adding `dog.reset()` here turns all
   * three red, and it was: session 53 wrote it, ran them, and put it back.
   *
   * So `respawn` means two different things. `docs/traps.md` carries the
   * consequence for anyone taking a measurement: **two arms of the same run
   * must REBOOT the page, not respawn** — through here the dog is the one thing
   * that does not go back, and "everything else held still" is quietly false
   * (D-100). If a beat ever needs the dog restored without a reload, that is a
   * new, named seam on `GameHandle` and those three gates keep this one.
   *
   * ── The queue resets HERE and deliberately not on the turn-back ───────
   *
   * The twelve go back in the huddle and the clock goes back to
   * not-yet-started. The dog's `placeRosalie` catch does NOT come through here
   * and must not: it pushes her up the field as a consequence, and a
   * consequence that also rewound eleven animals and the schedule would be a
   * fail state. Spec §5.6 says this beat has none. What being caught actually
   * costs is seconds off a clock that keeps running, which is the only currency
   * stage 4 has.
   */
  resetForRespawn(): void {
    // Respawn restores the authored field: stage 3 capture calls this path too.
    this.twelveOut.reset();
    this.queue.reset();
    this.#barrier = null;
    this.huddle.reset();
    // The queue and its undrained events are a pair, and a reset that restored
    // one of them plays a handler's whistle into a beat whose clock is back to
    // `null`. Found by an independent review; it is `TwelveOut.reset()`'s
    // standing warning in a third place.
    this.#cueQueue.length = 0;
    // The voice resets with the queue for exactly the reason above it: the
    // clock goes back to `null`, so a call scheduled against the old one would
    // either never come due or all come due at once on the next muster.
    this.#voice.reset();
  }

  // ── Render step ──────────────────────────────────────────────────────────

  /** See `#closing`: the prompt has to be gone on the frame she crosses the plane. */
  get closing(): boolean { return this.#closing; }
  /** What the touch button says `F` will do next. */
  get callVerb(): 'call' | 'send' { return this.twelveOut.snapshot().call === 'lie-up' ? 'call' : 'send'; }
  /** How many she got out, for the save's boundary. */
  get out(): number { return this.queue.snapshot().out; }

  /** With the other directors: the dog and its fade, the twelve and the whistles. */
  renderWorld(frameDt: number): void {
    // `in` and `null` both mean the overlay comes back: only `out` is black,
    // so this consequence needs no third presentation state.
    const dog = this.dog.snapshot();
    this.dogFade.setTarget(dog.turnBack.phase === 'out' ? 1 : 0);
    this.dogFade.update(frameDt);
    this.#dogRenderTime += frameDt;
    this.dogRoot.position.set(dog.x, hedgerowGroundY(dog.x, dog.z), dog.z);
    this.dogRoot.rotation.y = dog.facing;
    applyDogPose(this.dogParts, {
      walking: dog.phase === 'investigating' || dog.phase === 'returning' || dog.phase === 'driving',
      noseDown: false,
      casting: dog.phase === 'casting',
      tail: true,
      clock: this.#dogRenderTime,
    });
    this.#updatePant();
    // ── The twelve, and the clock they can hear ──────────────────────────
    //
    // Presentation reads a snapshot and writes matrices; it decides nothing.
    // Both halves are on the render step for the reason the beat's plan gives
    // in as many words: audio is drained on the render step and stealth is
    // emitted on the fixed step, which is the split D-161 paid for.
    //
    // One snapshot for both, so the bodies and the sound cannot describe two
    // different moments of the same herd.
    const herd = this.queue.snapshot();
    this.huddle.update(frameDt, herd);
    this.#voice.update(herd);
    while (this.#cueQueue.length > 0) {
      const cue = this.#cueQueue.shift();
      const at = cue === undefined ? undefined : CULVERT_CUE_AT[cue];
      if (at === undefined) continue;
      this.#audio.playOneShot('handler-whistle', { gain: at.gain, position: at.position });
    }
  }

  /**
   * After the listener has moved for this frame: the bell, the dog's snuffs and
   * Holly's body.
   *
   * **The bell is played here, on the render step, from what simulation
   * decided.** The events carry their own world position, so the sound is
   * where she was when she crossed the mark rather than where she is now —
   * and presentation cannot move the cadence, only voice it (D-045).
   *
   * The queue is drained whole every frame. A frame that ran several fixed
   * ticks plays several strokes, which is correct: they happened.
   */
  renderLate(frameDt: number): void {
    if (this.#bellQueue.length > 0) drainHollyBellQueue(this.#bellQueue, this.#playOneShot);
    for (const event of this.#dogEvents.splice(0)) {
      if (event.kind === 'cast') this.#audio.playOneShot('dog-snuff', { gain: 0.8, position: event.position });
    }
    this.hollyPresenter.update(frameDt, this.twelveOut.snapshot().holly, null, hedgerowGroundY);
  }

  /**
   * The dog's panting, while it walks. A silent handle is still a handle:
   * retrying while inactive is what makes the loop begin after the first user
   * gesture instead of staying muted.
   */
  #updatePant(): void {
    const dog = this.dog.snapshot();
    const moving = dog.phase === 'investigating' || dog.phase === 'returning' || dog.phase === 'driving';
    // See `#closed`. A dog still walking home when she goes through the
    // pipe kept panting under the fade, for the same reason the bed did.
    if (this.#closed || !moving) {
      this.#pant?.stop();
      this.#pant = null;
      return;
    }
    const at = { x: dog.x, y: hedgerowGroundY(dog.x, dog.z) + 0.4, z: dog.z };
    if (!this.#pant || !this.#pant.active) this.#pant = this.#audio.playLoop('dog-pant', { gain: 0.5, position: at });
    this.#pant.setPosition(at);
  }

  // ── Test seam ────────────────────────────────────────────────────────────

  /** `GameHandle.twelveOutDebug`'s body; its field notes live on that type. */
  debug(self: NightPlayer) {
    const state = this.twelveOut.snapshot();
    const stealth = this.stealth.snapshot();
    const watcher = stealth.watchers.find((item) => item.watcherId === 'night-dog');
    const dog = this.dog.snapshot();
    const herd = this.queue.snapshot();
    const huddle = this.huddle;
    const voice = this.#voice.debug;
    const bell = this.#lastBell;
    return {
      captures: this.#captures,
      holly: { x: state.holly.x, z: state.holly.z, yaw: state.holly.yaw, phase: state.holly.phase, moving: state.holly.moving, travelled: state.holly.travelledDistance },
      hollyY: this.hollyRoot.position.y, call: state.call, travelling: state.travelling,
      bell: bell ? { events: this.#bellEvents, lastThresholdDistance: bell.thresholdDistance, lastX: bell.x, lastZ: bell.z, radius: bell.radius } : null,
      // **`watcherY` is read back off the STEALTH SYSTEM and not recomputed**, because a
      // debug value derived from the same expression the caller passes in agrees with
      // itself whatever the caller stored. See `WatcherSuspicion.position`.
      dog: { x: dog.x, z: dog.z, facing: dog.facing, watcherY: watcher?.position.y ?? Number.NaN, suspicion: watcher?.suspicion ?? 0, state: watcher?.state ?? 'unaware', phase: dog.phase, targetX: dog.targetX, targetZ: dog.targetZ, distanceFromPost: dog.distanceFromPost, investigations: dog.investigations, drives: dog.drives, turnBack: dog.turnBack.phase },
      alarmLevel: stealth.alarmLevel,
      exposureAt: (x: number, z: number) => exposureAt({ x, y: hedgerowGroundY(x, z), z }, this.grid, this.cover).exposure,
      herd: {
        clock: herd.clock, out: herd.out, scattered: herd.scattered, queued: herd.queued,
        throughs: herd.throughs, headDistance: herd.headDistance, drawn: herd.drawn,
        ended: herd.ended, lastCue: herd.lastCue, serving: herd.serving,
        // The stall, as the harness can see it. `stalls` is the instrument and
        // `stalled` is not: a stall is a state a gate can sample between, and
        // `docs/traps.md` has a paragraph in capitals about exactly that.
        stalled: herd.stalled, stallReason: herd.stallReason, stalls: herd.stalls,
        blocked: herd.stallReason === 'blocked', barrier: this.#barrier,
        // Presentation's half, and it is here because nothing else can see it.
        // `audioDebug.oneShotStartsById['herd-stall']` counts the calls — read
        // off the engine that plays them — but the bed is a loop with a gain
        // ramp, and a gate that could not read this could not tell a bed that
        // is playing from one that was never started.
        bedPlaying: voice.bedPlaying,
        pantPlaying: this.#pant?.active === true,
        bedGain: voice.bedGain, bedPeak: voice.bedPeak, callsPending: voice.callsPending,
        states: herd.members.map((member) => member.state),
        distances: herd.members.map((member) => member.distance),
        drawnAt: (index: number) => huddle.positionOf(index),
        visible: huddle.visible,
        routeLength: CULVERT_ROUTE_LENGTH,
        lead: culvertRoutePlace(self.x, self.z),
      },
    };
  }

  /**
   * The room's objects and its loops go together. The bed is a LOOP: a room
   * torn down with it running leaves twelve animals walking through the next
   * beat, which is the same class of leak the pant was written for.
   */
  dispose(): void {
    this.#pant?.stop();
    this.#pant = null;
    this.#voice.reset();
    this.#cueQueue.length = 0;
    this.dogFade.dispose();
    this.huddle.dispose();
  }
}
